import { useEffect, useState, useRef } from "react";
import { motion, useMotionValue, useTransform, useSpring, useInView, useScroll } from "framer-motion";

/* ─── Scroll Progress Bar ─── */
export function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  return (
    <motion.div
      className="fixed top-0 left-0 right-0 h-[3px] z-[100] origin-left"
      style={{
        scaleX,
        background: "linear-gradient(90deg, #00F0FF, #7000FF, #00F0FF)"
      }}
    />
  );
}

/* ─── Typewriter Effect ─── */
export function TypewriterText({ text, className = "", delay = 0, speed = 40 }) {
  const [displayed, setDisplayed] = useState("");
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView || started) return;
    const timer = setTimeout(() => {
      setStarted(true);
      let i = 0;
      const interval = setInterval(() => {
        if (i <= text.length) {
          setDisplayed(text.slice(0, i));
          i++;
        } else {
          clearInterval(interval);
        }
      }, speed);
      return () => clearInterval(interval);
    }, delay);
    return () => clearTimeout(timer);
  }, [isInView, text, delay, speed, started]);

  return (
    <span ref={ref} className={className}>
      {displayed}
      {started && displayed.length < text.length && (
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{ repeat: Infinity, duration: 0.5 }}
          className="inline-block w-[3px] h-[0.9em] bg-[#00F0FF] ml-1 align-middle"
        />
      )}
    </span>
  );
}

/* ─── 3D Tilt Card ─── */
export function TiltCard({ children, className = "", intensity = 15, ...props }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const rotateX = useTransform(y, [-0.5, 0.5], [intensity, -intensity]);
  const rotateY = useTransform(x, [-0.5, 0.5], [-intensity, intensity]);
  const springRotateX = useSpring(rotateX, { stiffness: 200, damping: 20 });
  const springRotateY = useSpring(rotateY, { stiffness: 200, damping: 20 });

  const handleMouse = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleLeave = () => { x.set(0); y.set(0); };

  return (
    <div style={{ perspective: "800px" }}>
      <motion.div
        ref={ref}
        onMouseMove={handleMouse}
        onMouseLeave={handleLeave}
        style={{ rotateX: springRotateX, rotateY: springRotateY, transformStyle: "preserve-3d" }}
        whileHover={{
          scale: 1.04,
          boxShadow: "0 25px 50px -12px rgba(0,240,255,0.25), 0 0 30px -5px rgba(112,0,255,0.2)",
          borderColor: "rgba(0,240,255,0.5)"
        }}
        transition={{ duration: 0.3 }}
        className={`relative ${className}`}
        {...props}
      >
        {/* Inner glow that follows the mouse */}
        <motion.div
          className="absolute inset-0 opacity-0 pointer-events-none rounded-[inherit] transition-opacity duration-300 group-hover:opacity-100"
          style={{
            background: "radial-gradient(600px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(0,240,255,0.1), transparent 40%)"
          }}
        />
        {children}
      </motion.div>
    </div>
  );
}

/* ─── Magnetic Button ─── */
export function MagneticButton({ children, className = "", ...props }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 250, damping: 15 });
  const springY = useSpring(y, { stiffness: 250, damping: 15 });

  const handleMouse = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - rect.left - rect.width / 2) * 0.25);
    y.set((e.clientY - rect.top - rect.height / 2) * 0.25);
  };

  return (
    <motion.button
      ref={ref}
      onMouseMove={handleMouse}
      onMouseLeave={() => { x.set(0); y.set(0); }}
      style={{ x: springX, y: springY }}
      whileHover={{ scale: 1.06 }}
      whileTap={{ scale: 0.95 }}
      className={className}
      {...props}
    >
      {children}
    </motion.button>
  );
}

/* ─── Floating Particles ─── MUCH MORE VISIBLE */
export function FloatingParticles({ count = 40 }) {
  const particles = Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 4 + 2, // 2-6px (was 1-3px)
    duration: Math.random() * 15 + 10,
    delay: Math.random() * 8,
    color: i % 3 === 0 ? "#00F0FF" : i % 3 === 1 ? "#7000FF" : "#ffffff",
    opacity: i % 3 === 2 ? 0.25 : 0.5, // much more visible
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-[1]">
      {particles.map(p => (
        <motion.div
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            background: p.color,
            boxShadow: `0 0 ${p.size * 3}px ${p.color}`,
            opacity: p.opacity,
          }}
          animate={{
            y: [0, -80, 0],
            x: [0, Math.sin(p.id) * 40, 0],
            opacity: [p.opacity, p.opacity * 0.4, p.opacity],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            delay: p.delay,
            ease: "easeInOut"
          }}
        />
      ))}
    </div>
  );
}

/* ─── Animated Background Grid ─── MUCH MORE VISIBLE */
export function AnimatedGrid() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-[0]">
      {/* Grid lines */}
      <motion.div
        className="absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0,240,255,0.07) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,240,255,0.07) 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
        }}
        animate={{ backgroundPosition: ["0px 0px", "60px 60px"] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
      />
      {/* Glow spot 1 - Cyan */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 500, height: 500,
          background: "radial-gradient(circle, rgba(0,240,255,0.12) 0%, rgba(0,240,255,0.04) 40%, transparent 70%)",
          filter: "blur(40px)",
        }}
        animate={{ left: ["-5%", "70%", "-5%"], top: ["10%", "50%", "10%"] }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Glow spot 2 - Purple */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 400, height: 400,
          background: "radial-gradient(circle, rgba(112,0,255,0.1) 0%, rgba(112,0,255,0.03) 40%, transparent 70%)",
          filter: "blur(40px)",
        }}
        animate={{ left: ["75%", "5%", "75%"], top: ["55%", "15%", "55%"] }}
        transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  );
}

/* ─── Text Reveal Mask ─── */
export function TextReveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div
        initial={{ y: "110%" }}
        animate={isInView ? { y: 0 } : { y: "110%" }}
        transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.div>
    </div>
  );
}

/* ─── Scroll-triggered Section Wrapper ─── */
export function ScrollSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Stagger Children ─── */
export const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12, delayChildren: 0.15 }
  }
};

export const staggerItem = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: {
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

/* ─── Animated Counter ─── */
export function AnimatedCounter({ target, suffix = "", className = "" }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    const duration = 1500;
    const steps = 40;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isInView, target]);

  return <span ref={ref} className={className}>{count}{suffix}</span>;
}

/* ─── Gradient Divider ─── MUCH MORE VISIBLE */
export function GradientDivider() {
  return (
    <div className="relative h-[2px] overflow-hidden mx-auto max-w-5xl my-2">
      <div className="absolute inset-0 bg-zinc-900" />
      <motion.div
        className="absolute inset-y-0 w-1/2 h-full"
        style={{
          background: "linear-gradient(90deg, transparent, #00F0FF, #7000FF, transparent)",
          filter: "blur(1px)",
        }}
        animate={{ x: ["-50%", "200%"] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", repeatDelay: 0.5 }}
      />
    </div>
  );
}

/* ─── Animated Gradient Text ─── */
export function GradientText({ children, className = "" }) {
  return (
    <motion.span
      className={`inline-block bg-clip-text text-transparent ${className}`}
      style={{
        backgroundImage: "linear-gradient(90deg, #00F0FF, #7000FF, #FF00FF, #00F0FF)",
        backgroundSize: "300% 100%",
      }}
      animate={{ backgroundPosition: ["0% center", "100% center", "0% center"] }}
      transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
    >
      {children}
    </motion.span>
  );
}


/* ─── Morphing Background Blobs ─── */
export function MorphingBlobs({ colors = ["#00F0FF", "#7000FF", "#FF00FF"], className = "" }) {
  return (
    <div className={`absolute inset-0 pointer-events-none overflow-hidden z-[0] ${className}`}>
      {colors.map((color, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{
            width: `${50 + i * 10}%`,
            height: `${50 + i * 10}%`,
            background: `radial-gradient(circle, ${color}40 0%, ${color}20 30%, ${color}08 60%, transparent 80%)`,
            filter: "blur(50px)",
            left: `${5 + i * 20}%`,
            top: `${10 + (i % 2) * 25}%`,
          }}
          animate={{
            x: [0, 120 * (i % 2 ? 1 : -1), -100 * (i % 2 ? -1 : 1), 0],
            y: [0, -80 * (i % 2 ? -1 : 1), 100 * (i % 2 ? 1 : -1), 0],
            scale: [1, 1.3, 0.85, 1],
            borderRadius: ["30% 70% 70% 30%", "70% 30% 30% 70%", "50% 50% 70% 30%", "30% 70% 70% 30%"],
          }}
          transition={{
            duration: 14 + i * 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}


/* ─── Interactive Constellation Network ─── */
export function ConstellationNetwork({ nodeCount = 45, className = "" }) {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    let w, h;

    const resize = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w;
      canvas.height = h;
    };
    resize();
    window.addEventListener("resize", resize);

    const handleMouse = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const handleLeave = () => { mouseRef.current = { x: -1000, y: -1000 }; };
    canvas.addEventListener("mousemove", handleMouse);
    canvas.addEventListener("mouseleave", handleLeave);

    const cyanHex = [0, 240, 255];
    const purpleHex = [112, 0, 255];
    const nodes = Array.from({ length: nodeCount }, () => {
      const type = Math.random();
      return {
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        r: Math.random() * 2 + 2,
        rgb: type > 0.55 ? cyanHex : type > 0.25 ? purpleHex : [255, 255, 255],
      };
    });

    const connDist = 180;
    const mouseRadius = 200;

    const animate = () => {
      ctx.clearRect(0, 0, w, h);
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;

      // Draw all connections first (behind nodes)
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        const dma = Math.sqrt((a.x - mx) ** 2 + (a.y - my) ** 2);
        const aNear = dma < mouseRadius;

        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d >= connDist) continue;

          const dmb = Math.sqrt((b.x - mx) ** 2 + (b.y - my) ** 2);
          const bNear = dmb < mouseRadius;
          const bright = aNear || bNear;
          const fade = 1 - d / connDist;

          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          if (bright) {
            ctx.strokeStyle = `rgba(0,240,255,${fade * 0.7})`;
            ctx.lineWidth = 1.5 * fade + 0.5;
          } else {
            ctx.strokeStyle = `rgba(200,200,255,${fade * 0.12})`;
            ctx.lineWidth = 0.6;
          }
          ctx.stroke();
        }
      }

      // Draw nodes on top
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        const dx = n.x - mx;
        const dy = n.y - my;
        const dm = Math.sqrt(dx * dx + dy * dy);
        const nearMouse = dm < mouseRadius;

        // Mouse repulsion
        if (nearMouse && dm > 0) {
          const force = ((mouseRadius - dm) / mouseRadius) * 2;
          n.vx += (dx / dm) * force * 0.2;
          n.vy += (dy / dm) * force * 0.2;
        }

        n.vx *= 0.982;
        n.vy *= 0.982;
        n.x += n.vx;
        n.y += n.vy;

        if (n.x < -10) n.x = w + 10;
        if (n.x > w + 10) n.x = -10;
        if (n.y < -10) n.y = h + 10;
        if (n.y > h + 10) n.y = -10;

        const size = nearMouse ? n.r * 3 : n.r;
        const [r, g, b] = n.rgb;

        // Outer glow for near-mouse nodes
        if (nearMouse) {
          ctx.beginPath();
          ctx.arc(n.x, n.y, size * 4, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${r},${g},${b},0.08)`;
          ctx.fill();
        }

        // Node
        ctx.beginPath();
        ctx.arc(n.x, n.y, size, 0, Math.PI * 2);
        ctx.fillStyle = nearMouse ? `rgba(${r},${g},${b},1)` : `rgba(${r},${g},${b},0.5)`;
        ctx.fill();
      }

      // Mouse glow aura
      if (mx > 0 && my > 0) {
        const grad = ctx.createRadialGradient(mx, my, 0, mx, my, mouseRadius);
        grad.addColorStop(0, "rgba(0,240,255,0.08)");
        grad.addColorStop(0.5, "rgba(112,0,255,0.04)");
        grad.addColorStop(1, "rgba(0,0,0,0)");
        ctx.beginPath();
        ctx.arc(mx, my, mouseRadius, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
      }

      animId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("mousemove", handleMouse);
      canvas.removeEventListener("mouseleave", handleLeave);
    };
  }, [nodeCount]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full z-[1] ${className}`}
    />
  );
}
